// Audio remains in memory; the microphone is opened only after an explicit click.
export async function connectDynamicsLive({credentials, signal, initialPrompt, onStatus, onCaption, onUserTurn, onUserSpeech, onUserUtterance, onTool, onUsage}) {
  const {GoogleGenAI} = await import('@google/genai');
  let stream, input, output, processor, source, session, timer, next = 0;
  const playing = new Set();
  const clearAudio = () => { playing.forEach(s => { s.onended = null; try { s.stop(); } catch {} }); playing.clear(); next = 0; };
  let finish = () => {};
  const abort = () => { stream?.getTracks().forEach(t => t.stop()); clearAudio(); session?.close(); finish(); };
  signal.addEventListener('abort', abort, {once:true});
  try {
    if (signal.aborted) return;
    onStatus('connecting');
    if (!navigator.mediaDevices?.getUserMedia) throw new Error('Le microphone nécessite HTTPS ou localhost.');
    stream = await navigator.mediaDevices.getUserMedia({audio:{echoCancellation:true,noiseSuppression:true},video:false});
    if (signal.aborted) return;
    input = new AudioContext({sampleRate:16000}); output = new AudioContext({sampleRate:24000});
    await Promise.all([input.resume(),output.resume()]);
    await input.audioWorklet.addModule('/audio/dynamics-pcm.js');
    if (signal.aborted) return;
    source = input.createMediaStreamSource(stream);
    processor = new AudioWorkletNode(input, 'dynamics-pcm');
    const client = new GoogleGenAI({apiKey:credentials.token,httpOptions:{apiVersion:'v1alpha'}});
    let resolveReady, rejectReady;
    const ready = new Promise((resolve,reject) => { resolveReady=resolve; rejectReady=reject; });
    ready.catch(() => {});
    let fail;
    const done = new Promise((resolve,reject) => {finish=resolve; fail=reject;});
    // Attach a handler immediately: connection errors may precede the awaited setup.
    done.catch(() => {});
    let toolQueue = Promise.resolve(), userTranscript='';
    const flushUtterance = () => {
      const utterance=userTranscript.trim();userTranscript='';
      if(utterance) Promise.resolve(onUserUtterance?.(utterance)).catch(error=>fail(error));
    };
    const cancelled = new Set();
    const enqueueToolCall = call => {
      toolQueue=toolQueue.then(async()=>{
        if (signal.aborted || cancelled.has(call.id)) return;
        onStatus('thinking');
        let response;
        try { response=await onTool(call); } catch(error) {response={error:error.message};}
        if (!signal.aborted && !cancelled.has(call.id)) session.sendToolResponse({functionResponses:[{id:call.id,name:call.name,response:response || {error:'Aucun résultat.'}}]});
      }).catch(()=>{fail(new Error('La consultation du dashboard a échoué.'));});
    };
    session = await client.live.connect({model:credentials.model,config:credentials.config,callbacks:{
      onopen:() => {},
      onerror:() => {const error=new Error('Connexion vocale interrompue.'); rejectReady(error); fail(error);},
      onclose:() => {rejectReady(new Error('La connexion vocale est fermée.')); finish();},
      onmessage:message => {
        if (signal.aborted) return;
        if (message.setupComplete) resolveReady();
        if (message.usageMetadata) onUsage?.(message.usageMetadata);
        (message.toolCallCancellation?.ids || []).forEach(id => cancelled.add(id));
        const content = message.serverContent;
        if (content?.interrupted) {clearAudio(); onStatus('listening');}
        if (content?.interimInputTranscription?.text) onUserSpeech?.(content.interimInputTranscription.text.trim());
        if (content?.inputTranscription?.text) {
          if(!userTranscript)onUserTurn?.();
          const piece=content.inputTranscription.text;
          userTranscript=piece.startsWith(userTranscript)?piece:`${userTranscript}${piece}`;
          onUserSpeech?.(userTranscript.trim());
          onCaption('user',piece);
        }
        if (content?.inputTranscription?.finished) {
          flushUtterance();
        }
        // Live does not always send transcription.finished. Both model output
        // and a tool call establish that the user's turn has ended.
        if(content?.modelTurn || content?.turnComplete || message.toolCall) flushUtterance();
        if (content?.outputTranscription?.text) onCaption('assistant',content.outputTranscription.text);
        if (content?.turnComplete) onCaption('complete','');
        for (const part of content?.modelTurn?.parts || []) {
          if (!part.inlineData?.mimeType?.startsWith('audio/pcm')) continue;
          const bytes = Uint8Array.from(atob(part.inlineData.data),c => c.charCodeAt(0));
          if (bytes.length % 2) continue;
          const rate = Number(part.inlineData.mimeType.match(/rate=(\d+)/)?.[1] || 24000);
          const buffer = output.createBuffer(1,bytes.length/2,rate);
          const view = new DataView(bytes.buffer), samples = buffer.getChannelData(0);
          for (let i=0;i<samples.length;i++) samples[i]=view.getInt16(i*2,true)/32768;
          if (next-output.currentTime > 60) {fail(new Error('Réponse vocale trop longue. Relancez une conversation.')); return;}
          const sound = output.createBufferSource(); sound.buffer=buffer; sound.connect(output.destination); playing.add(sound);
          sound.onended=()=>{playing.delete(sound);if(!playing.size&&!signal.aborted)onStatus('listening');};
          next=Math.max(next,output.currentTime+.02); sound.start(next); next+=buffer.duration; onStatus('speaking');
        }
        for (const call of message.toolCall?.functionCalls || []) enqueueToolCall(call);
      },
    }});
    if (signal.aborted) return;
    const timeout=setTimeout(()=>rejectReady(new Error('La conversation vocale ne répond pas.')),15000);
    try {await ready;} finally {clearTimeout(timeout);}
    if (signal.aborted) return;
    if (initialPrompt?.trim()) {
      onUserSpeech?.(initialPrompt.trim());
      session.sendClientContent({turns:[{role:'user',parts:[{text:initialPrompt.trim()}]}],turnComplete:true});
      onCaption('user',initialPrompt.trim());
      onStatus('thinking');
    }
    processor.port.onmessage = event => {
      if (signal.aborted) return;
      const bytes=new Uint8Array(event.data);
      let binary=''; for (const byte of bytes) binary+=String.fromCharCode(byte);
      try {session.sendRealtimeInput({audio:{data:btoa(binary),mimeType:`audio/pcm;rate=${input.sampleRate}`}});} catch {fail(new Error('Envoi audio interrompu.'));}
    };
    source.connect(processor); processor.connect(input.destination);
    onStatus('listening');
    timer=setTimeout(()=>{onStatus('expired');finish();},credentials.maxSeconds*1000);
    await done;
  } finally {
    clearTimeout(timer); signal.removeEventListener('abort',abort); clearAudio();
    stream?.getTracks().forEach(t=>t.stop());
    if(processor){processor.port.onmessage=null;processor.disconnect();processor.port.close();}
    source?.disconnect(); session?.close();
    await Promise.all([input,output].map(ctx=>ctx&&ctx.state!=='closed'?ctx.close():Promise.resolve()));
  }
}
